import { Tag } from 'antd';
import { useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * ─── A ROW OF SMALL NEUTRAL LABELS, WITH AN OVERFLOW THAT DOES SOMETHING ─────────
 *
 * [D4.3] Built because a second consumer arrived. The four `+N`/"and N more" sites that
 * predate this are each hand-rolled at their call site and each renders plain text; this
 * one has to be reachable, so it is a component rather than a fifth copy.
 *
 * ⚠️ `+N` IS A BUTTON, NEVER A TOOLTIP, AND THAT IS THE WHOLE POINT OF THE COMPONENT.
 * A previous QA finding was a `+1 more` that looked like a link and did nothing. A tooltip
 * would fix the "does nothing" half and fail the other one: hover is unavailable on touch
 * and unreachable from the keyboard, which is the same objection this pass records against
 * hover-to-reveal on the officer surface. Clicking expands in place, and clicking again
 * collapses.
 *
 * ⚠️ AntD `Tag` WITH `className="m-0"`, which is the established pattern in this repo
 * (`ArrangementDetail`, `Sections`, `StandingOrderBuilder`) — its default margin is a
 * right margin that breaks a `gap`-based row. Deliberately NOT `StatusPill` or `StatusDot`:
 * those carry a TONE, and the design language forbids a list that is a wall of badges.
 */
export type Chip = {
  key: string;
  label: string;
  /** Renders muted with a suffix. Disclosed, never hidden — [FIX-1]. */
  muted?: boolean;
  /** The unabbreviated name, for a native `title`. */
  title?: string;
};

export type ChipListProps = {
  chips: readonly Chip[];
  /** How many to show before the overflow control. */
  max?: number;
  /** Names the control for a screen reader — "3 more columns on Festival policy". */
  overflowLabel: (hidden: number) => string;
  empty?: string;
  className?: string;
};

export const ChipList = ({ chips, max = 4, overflowLabel, empty = '—', className }: ChipListProps) => {
  const [expanded, setExpanded] = useState(false);

  if (chips[0] === undefined) return <span className="text-subtle">{empty}</span>;

  const hidden = Math.max(0, chips.length - max);
  const shown = expanded ? chips : chips.slice(0, max);

  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1', className)}>
      {shown.map((chip) => (
        <Tag key={chip.key} className={cn('m-0', chip.muted && 'opacity-60')} title={chip.title ?? chip.label}>
          {chip.label}
        </Tag>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          data-testid="chip-overflow"
          aria-expanded={expanded}
          aria-label={expanded ? 'Show fewer' : overflowLabel(hidden)}
          className="rounded px-1 text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
          onClick={(event) => {
            /*
             * ⚠️ The chips sit inside a table row whose first cell NAVIGATES. Without this,
             * expanding the overflow would also follow the row's link on any surface that
             * treats the row as clickable.
             */
            event.stopPropagation();
            setExpanded((open) => !open);
          }}
        >
          {expanded ? 'Show fewer' : `+${String(hidden)}`}
        </button>
      )}
    </span>
  );
};
